import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { api, ApiClientError } from "@/lib/api-client"
import { Button } from "@/components/ui/button"

const schema = z.object({
  email: z.string().email(),
})

type ForgotPasswordForm = z.infer<typeof schema>

export function ForgotPasswordPage() {
  const { t } = useTranslation()
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: ForgotPasswordForm) => {
    try {
      setError(null)
      await api.post("/api/v1/auth/password/reset-request", data)
      setSent(true)
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError("Wystąpił nieoczekiwany błąd")
      }
    }
  }

  if (sent) {
    return (
      <div className="space-y-4 text-center">
        <h2 className="page-title">
          {t("auth.resetPassword")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("auth.resetEmailSent")}
        </p>
        <Link
          to="/login"
          className="inline-block text-sm text-primary hover:underline"
        >
          {t("auth.backToLogin")}
        </Link>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <h2 className="page-title">
        {t("auth.forgotPassword")}
      </h2>
      <p className="text-sm text-muted-foreground">
        {t("auth.forgotPasswordDescription")}
      </p>

      {error && (
        <div
          className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300"
        >
          {error}
        </div>
      )}

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-muted-foreground">
          {t("auth.email")}
        </label>
        <input
          type="email"
          autoComplete="email"
          {...register("email")}
          className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none ${errors.email ? "border-red-500" : "border-input"}`}
        />
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? t("common.loading") : t("auth.sendResetLink")}
      </Button>

      <p className="text-center text-sm text-muted-foreground">
        <Link to="/login" className="text-primary hover:underline">
          {t("auth.backToLogin")}
        </Link>
      </p>
    </form>
  )
}
